import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateContact, type Contact } from '@/api/people'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

export function RenamePersonDialog({
  contact,
  onClose,
}: {
  contact: Contact
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(contact.name)
  const mutation = useMutation({
    mutationFn: () =>
      updateContact(contact.id, { name: name.trim(), note: contact.note ?? undefined }),
    onSuccess: () => {
      for (const key of ['contacts', 'personal-loans', 'transactions'])
        queryClient.invalidateQueries({ queryKey: [key] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="Rename"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) mutation.mutate()
        }}
        className="mx-4 w-full max-w-sm rounded-[20px] bg-[var(--fl-surface)] p-6 shadow-[rgba(0,0,0,0.15)_0px_8px_40px]"
      >
        <h2 className="mb-4 text-lg font-bold text-[var(--fl-text-1)]">Rename</h2>
        <label
          htmlFor="rename-person"
          className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
        >
          Name
        </label>
        <Input
          id="rename-person"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
        />
        {mutation.isError && (
          <p className="mt-1 text-xs text-[var(--fl-error)]">
            {(mutation.error as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail ?? 'Could not rename. Please try again.'}
          </p>
        )}
        <div className="mt-6 flex gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={mutation.isPending || !name.trim()}
            className="flex-1 font-semibold"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}
